import { Fragment, type ReactElement, useContext, useMemo, useState, useTransition } from "react"

import { useTranslation } from "react-i18next"
import { useQuery } from "react-query"
import { useSelector } from "react-redux"
import { useParams } from "react-router-dom"

import { Grid } from "@mui/material"
import type { BarDatum } from "@nivo/bar"
import type { InputChangeEvent } from "@synapse-analytics/synapse-ui"
import { Button, Skeleton } from "@synapse-analytics/synapse-ui"
import type { AxiosError, AxiosResponse } from "axios"
import axios from "axios"
import { useFormikContext } from "formik"

import { KonanEmptyState } from "../../../components/utility/KonanEmptyState"
import { KonanAPI } from "../../../services/KonanAPI"
import type { RootState } from "../../../store/ReduxStore"
import type { AutoMLJobLogs } from "../../../types/generated/api/AutoMLJobLogs"
import type { Model } from "../../../types/generated/api/Model"
import type { ModelSettingsSet } from "../../../types/generated/api/ModelSettingsSet"
import type { RetrainingJobLogs } from "../../../types/generated/api/RetrainingJobLogs"
import { Context } from "../Context"
import {
  CSVToJson,
  currentThresholdMetricsData,
  getAutoMLConfigHistoryData,
  getConfusionMatrixData,
  getCorrelationFunnelData,
  getDistributionChartData,
  getImportanceChartData,
  getLineChartData,
  getSankeyChartData,
} from "../Helpers"
import type { ConfigFormikProps, CurrentTab, Metrics, ThresholdData } from "../Interfaces"
import { Header } from "./Header/Header"
import { SelectFields } from "./SelectFields/SelectFields"
import { AutoMLV1 } from "./V1/AutoMLV1"
import { AutoMLV2 } from "./V2/AutoMLV2"

import styles from "./AutoMLContainer.module.scss"

/**
 * This component is the container component for the AutoML Configuration page.
 * It is responsible for fetching the data and passing it down to the components.
 * It also handles the state for the current tab and the current page of the importance chart.
 *
 * It renders a skeleton while the data is loading and displays a message if there is no data.
 * It renders the AutoML Configurations V2 component if the data is for the new format and the AutoML Configuration V1 component if the data is for the old format.
 *
 * @param {ModelSettingsSet} currentHistoryData - The current history data if the user is viewing a saved model.
 * @returns {ReactElement} - The JSX element to render.
 */
export function AutoMLConfigContainer(props: Readonly<{ currentHistoryData?: ModelSettingsSet }>): ReactElement {
  const { currentHistoryData } = props

  //#region Hooks region
  const { id: projectId } = useParams<{ id: string }>()

  // Context to hold the full Threshold Data Obj and the current selected threshold data to avoid prop drilling
  const { thresholdMetricsData, setThresholdMetricsData, setCurrentThresholdMetricData, currentThresholdMetricData } =
    useContext(Context)

  const {
    values: { negativeLabel, positiveLabel, negativeThreshold, positiveThreshold, singleThreshold, labels },
    setFieldValue,
  } = useFormikContext<ConfigFormikProps>()

  const currentModel = useSelector((state: RootState) => state.models.currentModel) as Model

  const [_, transition] = useTransition()

  const [currentDataTab, setCurrentDataTab] = useState<CurrentTab>("data")

  const [currentImportanceChartPage, setCurrentImportanceChartPage] = useState<number>(1)

  const [filteredImportanceData, setFilteredImportanceData] = useState<BarDatum[]>([])
  const { t } = useTranslation("ModelConfigurations")
  //#endregion

  //#region Select fields change handler to handel show the correct data when any select field change
  const selectLabelsFieldsChangeHandler = (e: InputChangeEvent<Element>): void => {
    if (!("value" in e.currentTarget) || typeof e.currentTarget.value !== "string" || !thresholdMetricsData) return

    const target = e.currentTarget
    const targetValue = e.currentTarget.value

    // Switch values when use switch any field
    if (target.id === "negativeLabel") {
      setFieldValue("negativeLabel", { name: targetValue })
      setFieldValue("positiveLabel", negativeLabel)
    } else {
      setFieldValue("negativeLabel", positiveLabel)
      setFieldValue("positiveLabel", { name: targetValue })
    }
  }
  //#endregion

  //#region The change handler for the sliders (positive slider, negative slider) for the new design and the single slider for the old design
  const sliderValueChangeHandler = (e: Event): void => {
    if (
      !e.target ||
      !("name" in e.target) ||
      !("value" in e.target) ||
      typeof e.target.value !== "number" ||
      !thresholdMetricsData?.thresholdMetrics
    )
      return

    const { thresholdMetrics } = thresholdMetricsData
    const targetSlider = e.target
    const sliderValue = e.target.value

    let currentMetricData: ThresholdData | undefined = {}

    // Handle changing the positive slider values
    if (targetSlider.name === "positive") {
      if (sliderValue <= negativeThreshold) return

      currentMetricData = currentThresholdMetricsData({
        thresholdMetrics: thresholdMetrics,
        positiveValue: sliderValue,
        negativeValue: negativeThreshold,
      })

      setFieldValue("positiveThreshold", sliderValue)
    }

    // Handle changing the negative slider values
    if (targetSlider.name === "negative") {
      if (sliderValue >= positiveThreshold) return

      currentMetricData = currentThresholdMetricsData({
        thresholdMetrics: thresholdMetrics,
        positiveValue: positiveThreshold,
        negativeValue: sliderValue,
      })

      setFieldValue("negativeThreshold", sliderValue)
    }

    // Handle changing the single slider value for old data
    if (targetSlider.name === "single_slider") {
      currentMetricData = currentThresholdMetricsData({
        thresholdMetrics: thresholdMetrics,
        singleSliderValue: sliderValue,
      })

      setFieldValue("singleThreshold", sliderValue)
    }

    setCurrentThresholdMetricData(currentMetricData)
  }
  //#endregion

  //#region Fetching data region

  // Fetch the CSV file form the presigned URL for the new model configurations
  const { refetch: thresholdMetricsRefetch, isFetching: isThresholdMetricsFetching } = useQuery<
    AxiosResponse<string>,
    AxiosError
  >({
    queryKey: ["threshold_metrics", currentModel?.uuid],
    queryFn: () => axios.get(currentModel?.threshold_metrics as string),
    onSuccess: ({ data }) => {
      // Convert the Fetched CSV string to JSON Obj with the threshold values as keys
      const thresholdMetricsData = CSVToJson(data)

      let autoMlModelLabels

      if (thresholdMetricsData.type === "V1") {
        // extracting AutoML model labels dynamically from the return
        // by checking the false values which should always be there
        // then extracting the values without the "false_" prefix
        autoMlModelLabels = Object.keys(Object.values(thresholdMetricsData.thresholdMetrics)[0] ?? {})
          .filter((key) => key.startsWith("false_"))
          .map((key) => key.replace("false_", ""))
        // setting the positive and negative labels in formik arbitrarily by order in the array
        setFieldValue("labels", autoMlModelLabels)
      }

      // Store the converted CSV Obj in the context
      setThresholdMetricsData(thresholdMetricsData)

      if (thresholdMetricsData.type === "V2") {
        if (!!currentHistoryData) {
          const { posItem, negItem, currentMetricsData } = getAutoMLConfigHistoryData(
            thresholdMetricsData.thresholdMetrics,
            thresholdMetricsData.type,
            currentHistoryData?.settings,
          )

          setFieldValue("positiveLabel", posItem?.label)
          setFieldValue("negativeLabel", negItem?.label)

          setFieldValue("negativeThreshold", negItem?.upper_bound)
          setFieldValue("positiveThreshold", posItem?.lower_bound)

          setCurrentThresholdMetricData(currentMetricsData)
        } else {
          const currentThresholdMetrics = currentThresholdMetricsData({
            thresholdMetrics: thresholdMetricsData.thresholdMetrics,
            positiveValue: positiveThreshold,
            negativeValue: negativeThreshold,
          })

          setCurrentThresholdMetricData(currentThresholdMetrics)
        }
      } else {
        if (!!currentHistoryData) {
          const { posItem, negItem, currentMetricsData } = getAutoMLConfigHistoryData(
            thresholdMetricsData.thresholdMetrics,
            thresholdMetricsData.type,
            currentHistoryData?.settings,
          )

          setFieldValue("positiveLabel", posItem?.label)
          setFieldValue("negativeLabel", negItem?.label)

          setFieldValue("singleThreshold", negItem?.upper_bound)

          setCurrentThresholdMetricData(currentMetricsData)
        } else {
          const currentThresholdMetrics = currentThresholdMetricsData({
            thresholdMetrics: thresholdMetricsData.thresholdMetrics,
            singleSliderValue: singleThreshold,
          })

          if (autoMlModelLabels) {
            setFieldValue("positiveLabel", { name: autoMlModelLabels[0] })
            setFieldValue("negativeLabel", { name: autoMlModelLabels[1] })
          }

          setCurrentThresholdMetricData(currentThresholdMetrics)
        }
      }
    },
    enabled: !!currentModel?.threshold_metrics,
  })

  // Fetch metrics data from the presigned URL for new model configurations
  const { data: metrics, isFetching: isMetricsFetching } = useQuery<AxiosResponse<Metrics>, AxiosError>({
    queryKey: ["metrics", currentModel?.uuid],
    queryFn: () => axios.get(currentModel?.metrics as string),
    onSuccess: ({ data }) => {
      if (data?.target_col_mapping?.pos_label) {
        const posLabel = data.target_col_mapping.pos_label
        const negLabel = data.target_col_mapping.neg_label

        setFieldValue("labels", [posLabel, negLabel])

        setFieldValue("positiveLabel", { name: posLabel })
        setFieldValue("negativeLabel", { name: negLabel })
      }
    },
    enabled: !!currentModel?.metrics,
  })

  // Fetch training logs data if training_job key returned from the backend with value to show training log
  const { isFetching: isTrainingLogsFetching, data: trainingLogs } = useQuery<RetrainingJobLogs, AxiosError>(
    ["retraining-logs", projectId, currentModel?.retraining_job],
    () => KonanAPI.fetchTrainingModelLogs(projectId as string, currentModel?.retraining_job as string),
    {
      enabled: !!projectId && !!currentModel?.retraining_job && thresholdMetricsData?.type === "V2",
    },
  )
  // Fetch training logs data if automl_training_job key returned from the backend with value to show training log
  const { isFetching: isTrainingAutoMLLogsFetching, data: trainingAutomlLogs } = useQuery<AutoMLJobLogs, AxiosError>(
    ["training-automl-logs", projectId, currentModel?.automl_training_job],
    () => KonanAPI.fetchAutoMLTrainingJobLogs(projectId as string, currentModel?.automl_training_job as string),
    {
      enabled: !!projectId && !!currentModel?.automl_training_job && thresholdMetricsData?.type === "V2",
    },
  )

  //#endregion

  //#region handle search logic for the importance chart
  const importanceChartSearchInputHandler = (e: InputChangeEvent): void => {
    setCurrentImportanceChartPage(1)
    transition(() => {
      const value = e.target.value
      if (typeof value !== "string") return
      const filteredData = importanceChartData.filter((data) => {
        if (value.length === 0) return true
        return data.label?.toString()?.toLowerCase()?.includes(value?.toLowerCase())
      })
      setFilteredImportanceData(filteredData)
    })
  }
  //#endregion

  const { sankeyData, correlationFunnelData, distributionChartData, lineChartData, confusionMatrixData } =
    useMemo(() => {
      // Handle getting confusionMatrix formatted data
      const confusionMatrixData = getConfusionMatrixData(
        thresholdMetricsData?.type,
        currentThresholdMetricData,
        metrics?.data?.target_col_mapping?.pos_label,
        metrics?.data?.target_col_mapping?.neg_label,
        labels,
        t,
      )

      // Handle getting sankey chart formatted data
      const sankeyData = getSankeyChartData(currentThresholdMetricData, positiveLabel?.name, negativeLabel?.name)

      // Handle getting the line chart formatted data
      const lineChartData = getLineChartData(t, positiveLabel?.name, metrics?.data)

      // Handle getting the distribution chart formatted data
      const distributionChartData = getDistributionChartData(t, positiveLabel?.name, negativeLabel?.name, metrics?.data)

      // Handle getting the correlation Funnel chart formatted data
      const correlationFunnelData = getCorrelationFunnelData(positiveLabel?.name, metrics?.data)

      return { sankeyData, lineChartData, distributionChartData, correlationFunnelData, confusionMatrixData }
    }, [
      thresholdMetricsData?.type,
      currentThresholdMetricData,
      positiveLabel?.name,
      negativeLabel?.name,
      labels,
      t,
      metrics?.data,
    ])

  // Handle getting importance bar chart formatted data and setting the state for the filtered data
  const importanceChartData = useMemo(() => {
    const importanceChartData = getImportanceChartData(metrics?.data)
    setFilteredImportanceData(importanceChartData)
    return importanceChartData
  }, [metrics?.data])

  //#endregion

  // check if any request on the page is still loading
  const isPageLoading =
    isThresholdMetricsFetching || isMetricsFetching || isTrainingAutoMLLogsFetching || isTrainingLogsFetching

  return (
    <div className={styles.autoMLContainer}>
      {isPageLoading ? (
        <Fragment>
          <Grid
            container
            gap="1rem"
            justifyContent="center"
            alignItems="center"
            marginTop="1rem"
            flexWrap="nowrap"
            marginBottom="2rem"
          >
            <Skeleton height={60} />
            <Skeleton height={60} />
          </Grid>
          <Grid
            container
            direction="column"
            justifyContent="flex-start"
            alignItems="flex-start"
            className={"full-height"}
            gap="2rem"
            marginBottom="1rem"
          >
            <Skeleton width="100%" height={140} />
            <Skeleton width="30%" />
            <Skeleton width="60%" height={30} />
            <Skeleton width="75%" />
            <Skeleton width="100%" height={150} />
          </Grid>
        </Fragment>
      ) : !thresholdMetricsData?.thresholdMetrics ? (
        <div className={styles.emptyStateContainer}>
          <KonanEmptyState
            title={t("emptyState.unexpectedIssue.title")}
            subTitle={t("emptyState.unexpectedIssue.subtitle")}
            actionButtons={[
              <Button variant="primary" onClick={thresholdMetricsRefetch}>
                {t("emptyState.unexpectedIssue.actions.refresh")}
              </Button>,
            ]}
          />
        </div>
      ) : (
        <div>
          <Header mode={!!currentHistoryData ? "view" : "edit"} />
          <SelectFields
            onChangeHandler={selectLabelsFieldsChangeHandler}
            mode={!!currentHistoryData ? "view" : "edit"}
          />
          {thresholdMetricsData.type === "V2" ? (
            <AutoMLV2
              sliderValueChangeHandler={sliderValueChangeHandler}
              setCurrentDataTab={setCurrentDataTab}
              setCurrentImportanceChartPage={setCurrentImportanceChartPage}
              importanceChartSearchInputHandler={importanceChartSearchInputHandler}
              disabled={!currentModel?.threshold_metrics || !!currentHistoryData}
              currentDataTab={currentDataTab}
              sankeyChartData={sankeyData}
              importanceChartData={filteredImportanceData}
              lineChartData={lineChartData}
              distributionChartData={distributionChartData}
              correlationFunnelData={correlationFunnelData}
              confusionMatrixData={confusionMatrixData}
              currentImportanceChartPage={currentImportanceChartPage}
              trainingLogs={trainingAutomlLogs?.logs || trainingLogs?.logs}
            />
          ) : (
            <AutoMLV1
              confusionMatrixData={confusionMatrixData}
              modelId={currentModel?.uuid || ""}
              onChangeHandler={sliderValueChangeHandler}
              disabled={!!currentHistoryData}
            />
          )}
        </div>
      )}
    </div>
  )
}
